import Coordinate from "../shapes/support/Coordinate";
import AbstractProvidedClass from "../shared/AbstractProvidedClass";

export default class View extends AbstractProvidedClass {
    maxScale = 10;
    minScale = 0.005;
    zoomFactor = 1.2;
    posX = 120;
    posY = 120;
    scale = 1;

    init({ scale = 1, posX = 120, posY = 120 } = {}) {
        this.scale = scale;
        this.posX = posX;
        this.posY = posY;
    }

    reset() {
        [this.scale, this.posX, this.posY] = this.calculateOverview(this.p.width, this.p.height);

        this.grid.recalculate();
        this.store();
    }

    calculateOverview(width, height) {
        if (this.elements.length == 0) {
            return [1, 120, 120];
        }
        let [minX, minY, maxX, maxY] = this.elements.getMinMax();
        let dX = maxX - minX;
        let dY = maxY - minY;
        const padding = 0.15
        minX = minX - padding * dX;
        maxX = maxX + padding * dX;
        minY = minY - padding * dY;
        maxY = maxY + padding * dY;
        dX = maxX - minX;
        dY = maxY - minY;

        const scaleX = width / dX;
        const scaleY = height / dY;

        const scale = Math.min(scaleX, scaleY);
        const posX = -minX * scale + (width - dX * scale) / 2;
        const posY = -minY * scale + (height - dY * scale) / 2;
        return [scale, posX, posY];
    }

    zoomIn(mPosX, mPosY, zoomFactor = this.zoomFactor) {
        if (this.scale >= this.maxScale) {
            return;
        }

        this.scale *= zoomFactor;
        this.posX = Math.round(this.posX * zoomFactor
            - (mPosX ?? this.p.width / 2) * (zoomFactor - 1));
        this.posY = Math.round(this.posY * zoomFactor
            - (mPosY ?? this.p.height / 2) * (zoomFactor - 1));

        this.grid.recalculate();
        this.store();
    }

    zoomOut(mPosX, mPosY, zoomFactor = this.zoomFactor) {
        if (this.scale <= this.minScale) {
            return;
        }

        this.scale /= zoomFactor;
        this.posX = Math.round((
            this.posX + (mPosX ?? this.p.width / 2) * (zoomFactor - 1)
        ) / zoomFactor);
        this.posY = Math.round((
            this.posY + (mPosY ?? this.p.height / 2) * (zoomFactor - 1)
        ) / zoomFactor);

        this.grid.recalculate();
        this.store();
    }

    move(xMovement, yMovement) {
        this.posX += Math.round(xMovement);
        this.posY += Math.round(yMovement);
        this.store();
    }

    getRelative(x, y, rotation) {
        let d = this.p.dist(this.currentX, this.currentY, x, y);
        let a = Math.atan2(this.currentY - y, this.currentX - x);
        return [d * Math.cos(a - rotation), d * Math.sin(a - rotation), a, d];
    }

    doubleTouchStarted(touches) {
        this.lastDist = dist(touches[0].x, touches[0].y, touches[1].x, touches[1].y);
        this.lastCenterX = (touches[0].x + touches[1].x) / 2;
        this.lastCenterY = (touches[0].y + touches[1].y) / 2;
    }

    doubleTouchMoved(touches) {
        let centerX = (touches[0].x + touches[1].x) / 2;
        let centerY = (touches[0].y + touches[1].y) / 2;

        this.posX += centerX - this.lastCenterX;
        this.posY += centerY - this.lastCenterY;

        let currentDist = dist(touches[0].x, touches[0].y, touches[1].x, touches[1].y);
        this.scale *= constrain(this.scale, this.minScale, this.maxScalecon);

        this.lastDist = currentDist;
        this.lastCenterX = centerX;
        this.lastCenterY = centerY;
    }

    get snappedAbsX() {
        return Math.round(this.snappedRelX / this.scale);
    }

    get snappedAbsY() {
        return Math.round(this.snappedRelY / this.scale);
    }

    get snappedRelX() {
        return this.settings.snapToGrid
            ? Math.round(this.currentX / this.grid.minorSize) * this.grid.minorSize
            : Math.round(this.currentX);
    }

    get snappedRelY() {
        return this.settings.snapToGrid
            ? Math.round(this.currentY / this.grid.minorSize) * this.grid.minorSize
            : Math.round(this.currentY);
    }

    get absX() {
        return Math.round(this.currentX / this.scale);
    }

    get absY() {
        return Math.round(this.currentY / this.scale);
    }

    get currentX() {
        return this.p.mouseX - this.posX;
    }

    get currentY() {
        return this.p.mouseY - this.posY;
    }

    get snappedCoordinate() {
        return new Coordinate(this.provider, this.snappedAbsX, this.snappedAbsY);
    }

    get coordinate() {
        return new Coordinate(this.provider, this.absX, this.absY);
    }

    export() {
        return { scale: this.scale, posX: this.posX, posY: this.posY };
    }

    store() {
        if (this.storage) {
            this.storage.save();
        }
    }
}
